import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckIcon, FileCheckIcon, MoonIcon, SunIcon, TentIcon, TicketPercentIcon, UsersIcon, WalletIcon } from 'lucide-react';
import { formatCurrency, pluralize } from '../../utils/format';

/*
 * The summary cards. Every figure comes from utils/registrations summarize: booking money is added
 * once per booking (a booking's rows share it), and collected % = paid / (paid + balance due),
 * because booking total is not the price on payment-plan and pay-later bookings.
 */

const ease = [0.23, 1, 0.32, 1];

function Value({ children, k }) {
  return (
    <motion.span key={k} initial={{ opacity: 0.35, y: 3 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease }} className="tw-inline-block">
      {children}
    </motion.span>
  );
}

function CardLabel({ icon: Icon, children, inverted = false }) {
  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
      <p className={`tw-text-sm ${inverted ? 'tw-text-white/75' : 'tw-text-neutral-500'}`}>{children}</p>
      <span
        className={`tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg ${inverted ? 'tw-bg-white/10 tw-text-white' : 'tw-bg-brand-50 tw-text-brand-600'}`}
        aria-hidden>
        <Icon className="tw-h-3.5 tw-w-3.5" />
      </span>
    </div>
  );
}

const card = 'tw-flex tw-min-h-[148px] tw-flex-col tw-rounded-2xl tw-border tw-border-neutral-200 tw-bg-white tw-p-5 tw-shadow-sm';
const big = 'tw-mt-1.5 tw-text-[28px] tw-font-semibold tw-leading-none tw-tabular-nums tw-text-neutral-900';

export function scopeLabel(scope, count) {
  if (scope === 'selected') return `Totals for ${pluralize(count, 'selected registration')}`;
  if (scope === 'filtered') return `Totals for ${pluralize(count, 'filtered registration')}`;
  return `Totals for all ${count} registrations`;
}

export function SummaryCards({ summary: s, scope, onClearSelection, onClearFilters, onShowMissingDocuments }) {
  const stayTotal = s.overnight + s.commuter;
  return (
    <section aria-labelledby="reports-summary-heading">
      <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-2">
        <h2 id="reports-summary-heading" className="tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900">
          Summary
        </h2>
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs" aria-live="polite">
          {scope !== 'all' && <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-brand-600" aria-hidden />}
          <span className={scope === 'all' ? 'tw-text-neutral-400' : 'tw-font-medium tw-text-brand-600'}>{scopeLabel(scope, s.count)}</span>
          {scope === 'selected' && (
            <button type="button" onClick={onClearSelection} className="tw-text-neutral-500 tw-underline-offset-2 hover:tw-text-neutral-900 hover:tw-underline">
              Clear selection
            </button>
          )}
          {scope === 'filtered' && (
            <button type="button" onClick={onClearFilters} className="tw-text-neutral-500 tw-underline-offset-2 hover:tw-text-neutral-900 hover:tw-underline">
              Show all
            </button>
          )}
        </div>
      </div>

      <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-[1.55fr_repeat(4,minmax(0,1fr))]">
        <div className="tw-flex tw-min-h-[148px] tw-flex-col tw-rounded-2xl tw-bg-brand-700 tw-p-5 tw-text-white tw-shadow-sm sm:tw-col-span-2 lg:tw-col-span-1">
          <CardLabel icon={WalletIcon} inverted>
            Amount due
          </CardLabel>
          <p className="tw-mt-1.5 tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2">
            <span className="tw-text-[32px] tw-font-semibold tw-leading-none tw-tracking-tight tw-tabular-nums">
              <Value k={s.amountDue}>{formatCurrency(s.amountDue)}</Value>
            </span>
            <span className="tw-text-sm tw-text-white/70">across {pluralize(s.bookingCount, 'booking')}</span>
          </p>
          <div className="tw-mt-auto tw-pt-4">
            <div
              className="tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-white/15"
              role="progressbar"
              aria-valuenow={s.collectedPct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Collected">
              <motion.div className="tw-h-full tw-rounded-full tw-bg-white" initial={false} animate={{ width: `${s.collectedPct}%` }} transition={{ duration: 0.3, ease }} />
            </div>
            <p className="tw-mt-2 tw-text-xs tw-text-white/75">
              <span className="tw-font-medium tw-text-white">{formatCurrency(s.collected)}</span> collected · {s.collectedPct}%
            </p>
          </div>
        </div>

        <div className={card}>
          <CardLabel icon={UsersIcon}>Registered</CardLabel>
          <p className={big}>
            <Value k={s.registered}>{s.registered}</Value>
          </p>
          <p className="tw-mt-auto tw-pt-4 tw-text-xs tw-text-neutral-500">{s.cancelled} cancelled</p>
        </div>

        <div className={card}>
          <CardLabel icon={TentIcon}>Overnight / Commuter</CardLabel>
          <p className={big}>
            <Value k={`${s.overnight}-${s.commuter}`}>
              {s.overnight} <span className="tw-font-normal tw-text-neutral-400">/</span> {s.commuter}
            </Value>
          </p>
          <div className="tw-mt-auto tw-pt-4">
            <div className="tw-flex tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-amber-100">
              <motion.div
                className="tw-h-full tw-bg-blue-600"
                initial={false}
                animate={{ width: stayTotal ? `${(s.overnight / stayTotal) * 100}%` : '0%' }}
                transition={{ duration: 0.3, ease }}
              />
            </div>
            <p className="tw-mt-2 tw-flex tw-items-center tw-gap-3 tw-text-xs tw-text-neutral-500">
              <span className="tw-inline-flex tw-items-center tw-gap-1">
                <MoonIcon className="tw-h-3 tw-w-3 tw-text-blue-700" aria-hidden />
                Overnight
              </span>
              <span className="tw-inline-flex tw-items-center tw-gap-1">
                <SunIcon className="tw-h-3 tw-w-3 tw-text-amber-600" aria-hidden />
                Commuter
              </span>
            </p>
          </div>
        </div>

        <div className={card}>
          <CardLabel icon={FileCheckIcon}>Documents complete</CardLabel>
          <p className={big}>
            <Value k={`${s.documentsComplete}-${s.registered}`}>
              {s.documentsComplete} <span className="tw-text-lg tw-font-normal tw-text-neutral-400">/ {s.registered}</span>
            </Value>
          </p>
          <div className="tw-mt-auto tw-pt-4">
            {s.missingDocuments > 0 ? (
              <button
                type="button"
                onClick={onShowMissingDocuments}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-amber-700 tw-underline-offset-2 hover:tw-underline">
                <AlertCircleIcon className="tw-h-3.5 tw-w-3.5" aria-hidden />
                {pluralize(s.missingDocuments, 'participant')} missing documents
              </button>
            ) : (
              <p className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-green-700">
                <CheckIcon className="tw-h-3.5 tw-w-3.5" aria-hidden />
                All documents in
              </p>
            )}
          </div>
        </div>

        <div className={card}>
          <CardLabel icon={TicketPercentIcon}>Coupon discounts</CardLabel>
          <p className={big}>
            <Value k={s.couponTotal}>{formatCurrency(s.couponTotal)}</Value>
          </p>
          <p className="tw-mt-auto tw-pt-4 tw-text-xs tw-text-neutral-500">{pluralize(s.couponCount, 'booking')} with a coupon</p>
        </div>
      </div>
    </section>
  );
}
